import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import styled from 'styled-components';
import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import { anchoredStampSizePt } from '@dealstack/shared/lib/common/business/stampGeometry';
import { ptToMm } from '@dealstack/shared/lib/common/documentLayout/documentLayoutConstants';
import { PALETTE } from '@dealstack/shared/lib/common/business/stampDesign';
import { StampPreview } from '@dealstack/frontend/shared/components/StampPreview/StampPreview.tsx';

import { signatureNodeSlotKey } from './signatureNode';
import type { SignatureNodeOptions } from './signatureNode';
import { geometryAttributes, movedGeometry, resizedGeometry } from './signatureGeometry';
import { pairSignature, SIGNATURE_STAMP_ATTRIBUTE, signaturePairTarget } from './signaturePairing';
import { relocateAnchor, signatureInsertPoint } from './signatureDrop';
import { setAnchorDropHint } from './signaturePairHighlight';
import type { DropHint } from './signaturePairHighlight';
import { autoScroll, scrollableAncestor } from './dragScroll';
import type { SignatureGeometry } from './signatureGeometry';

/** How far the pointer has to leave the stamp's own line before the drag becomes a move. */
const RELOCATE_THRESHOLD_PX = 10;

/** Geometry mid-gesture, plus how far the stamp has been lifted off its own line. */
interface Draft {
	geometry: SignatureGeometry;
	liftYPx: number;
}

/**
 * A signature anchor, drawn as the stamp it will become, sized and positioned by its author.
 *
 * Both dimensions are stated in MILLIMETRES to match the space `flowRender` reserves — the
 * editor surface is a page-shaped `mm` box for the same reason, so the anchor takes up the same
 * fraction of the line length here as it will in the generated PDF.
 *
 * The anchor keeps its own full-width band whatever the author does to it. That is what makes
 * "move it anywhere" safe: nothing wraps beside a stamp, so no offset and no size can put one
 * on top of a clause. Moving it UP or DOWN is moving it in the content — the grip hands over to
 * ProseMirror's own node drag, which is also what redraws the text around its new home.
 */
export const createSignatureNodeView =
	(options: SignatureNodeOptions) =>
	({ node, selected, updateAttributes, editor, getPos }: NodeViewProps) => {
		const showMetadata = options.showStampMetadata ?? true;
		const slotKey = signatureNodeSlotKey(node.attrs);
		const display = options.displayFor?.(slotKey);
		const isWitness = node.attrs.isWitness === true;

		// The same sizer the renderer reserves space with, so the anchor takes up the same room
		// on screen as it will in the generated PDF — a witness's box included, which is taller
		// and therefore has its own ratio to be resized along.
		const designPt = anchoredStampSizePt(showMetadata, isWitness);
		const designWidthMm = ptToMm(designPt.width);
		const ratio = designPt.width / designPt.height;

		const bandRef = useRef<HTMLDivElement>(null);
		const stampRef = useRef<HTMLDivElement>(null);
		/**
		 * Geometry mid-gesture, committed on release.
		 *
		 * A transaction per pointer-move would put a hundred steps in the undo history for one
		 * drag, and `onUpdate` re-parses the whole document on each. Held here instead, so the
		 * band resizes and the text below it reflows live while the document sees one edit.
		 */
		const [draft, setDraft] = useState<Draft | null>(null);

		const storedWidthMm = typeof node.attrs.widthMm === 'number' ? node.attrs.widthMm : null;
		const widthMm = draft?.geometry.widthMm ?? storedWidthMm ?? designWidthMm;
		const offsetXMm =
			draft?.geometry.offsetXMm ??
			(typeof node.attrs.offsetXMm === 'number' ? node.attrs.offsetXMm : 0);
		const heightMm = widthMm / ratio;

		const selectSelf = (): void => {
			const pos = getPos();
			if (typeof pos === 'number') {
				editor.commands.setNodeSelection(pos);
			}
		};

		/**
		 * What the gesture would do if released now: pair with another anchor, or land on a line
		 * of its own. Null when the pointer is over nowhere an anchor can go, which leaves it
		 * where it started rather than dropping it somewhere arbitrary.
		 */
		const dropHintAt = (clientX: number, clientY: number): DropHint | null => {
			const pos = getPos();
			if (typeof pos !== 'number') {
				return null;
			}
			const { view } = editor;
			const source = { from: pos, to: pos + node.nodeSize };
			const target = signaturePairTarget(view, { clientX, clientY });
			if (target !== null && pairSignature(view.state, target, node, source) !== null) {
				return { kind: 'pair', target };
			}
			const coords = view.posAtCoords({ left: clientX, top: clientY });
			if (coords === null) {
				return null;
			}
			// Resolved to where a BLOCK can go before it is used, not left as the text position
			// under the pointer: the line is drawn at this position, and mid-paragraph is not
			// where the anchor would land — it goes after that paragraph.
			const landing = signatureInsertPoint(view.state.doc, view.state.schema, coords.pos);
			if (landing === null) {
				return null;
			}
			// Asked by building the transaction rather than by a cheaper test: what the drop
			// will do and what the hint promises are then the same answer by construction.
			return relocateAnchor(view.state, source, landing, node) === null
				? null
				: { kind: 'insert', pos: landing };
		};

		const beginGesture = (
			event: ReactPointerEvent<HTMLElement>,
			gesture: 'stamp' | 'resize'
		): void => {
			const band = bandRef.current;
			const stamp = stampRef.current;
			if (!editor.isEditable || band === null || stamp === null || event.button !== 0) {
				return;
			}
			const stampWidthPx = stamp.getBoundingClientRect().width;
			if (stampWidthPx === 0) {
				return;
			}
			// Derived from an element whose millimetre width is already known, rather than from
			// the CSS mm constant: whatever sits between this node and the page — a transform, a
			// zoom — is in the ratio for free, and the drag cannot drift from the pointer.
			const mmPerPx = widthMm / stampWidthPx;
			const bandWidthMm = band.getBoundingClientRect().width * mmPerPx;
			const startX = event.clientX;
			const startY = event.clientY;
			const start: SignatureGeometry = { widthMm, offsetXMm };
			const scroller = scrollableAncestor(stamp);

			event.preventDefault();
			event.stopPropagation();
			selectSelf();
			const target = event.currentTarget;
			target.setPointerCapture(event.pointerId);

			/**
			 * Sticky once entered. Deciding per frame would flip the anchor between moving
			 * across its own line and moving to another one every time the pointer wandered back
			 * through the threshold, with the document rearranging underneath it.
			 */
			let relocating = false;
			let hint: DropHint | null = null;

			const geometryAt = (clientX: number): SignatureGeometry => {
				const deltaMm = (clientX - startX) * mmPerPx;
				return gesture === 'resize'
					? resizedGeometry(start, deltaMm, bandWidthMm)
					: movedGeometry(start, deltaMm, bandWidthMm);
			};

			const onMove = (moveEvent: PointerEvent): void => {
				const geometry = geometryAt(moveEvent.clientX);
				if (gesture === 'resize') {
					setDraft({ geometry, liftYPx: 0 });
					return;
				}
				relocating =
					relocating || Math.abs(moveEvent.clientY - startY) > RELOCATE_THRESHOLD_PX;
				if (!relocating) {
					setDraft({ geometry, liftYPx: 0 });
					return;
				}
				autoScroll(scroller, moveEvent.clientY);
				hint = dropHintAt(moveEvent.clientX, moveEvent.clientY);
				setAnchorDropHint(editor.view, hint);
				// The stamp follows the pointer, so what is being carried is never in doubt, and
				// horizontal movement keeps feeding the offset — the x it is let go at is the x
				// it keeps, which makes this one gesture rather than a move and then a nudge.
				setDraft({ geometry, liftYPx: moveEvent.clientY - startY });
			};

			const onEnd = (endEvent: PointerEvent): void => {
				window.removeEventListener('pointermove', onMove);
				window.removeEventListener('pointerup', onEnd);
				window.removeEventListener('pointercancel', onEnd);
				setDraft(null);
				setAnchorDropHint(editor.view, null);
				const attributes = geometryAttributes(geometryAt(endEvent.clientX), designWidthMm);
				const pos = getPos();

				// Never lifted, or lifted over nowhere it can go: the anchor stays where it is
				// and only its own geometry changes, which is what moving it across its own line
				// means anyway. A drag that found no home is a no-op, never a deletion.
				if (!relocating || hint === null || typeof pos !== 'number') {
					updateAttributes(attributes);
					return;
				}

				const { view } = editor;
				const source = { from: pos, to: pos + node.nodeSize };
				// Carried ON the node rather than written afterwards: its position moves in this
				// same transaction, so a second one keyed on the old position would write the
				// attributes to whatever had taken its place.
				const moved = node.type.create({ ...node.attrs, ...attributes });
				const transaction =
					hint.kind === 'pair'
						? pairSignature(view.state, hint.target, moved, source)
						: relocateAnchor(view.state, source, hint.pos, moved);
				if (transaction === null) {
					updateAttributes(attributes);
					return;
				}
				view.dispatch(transaction);
			};

			// On the WINDOW, not on the stamp. The stamp stops taking pointer events the moment
			// it is lifted — it has to, or it is the thing every hit test finds — and whether a
			// captured element still receives them once it does is not worth depending on. A
			// captured pointer's events propagate up regardless, so this hears them either way.
			window.addEventListener('pointermove', onMove);
			window.addEventListener('pointerup', onEnd);
			window.addEventListener('pointercancel', onEnd);
		};

		return (
			<NodeViewWrapper
				as="div"
				data-signature-slot=""
				data-slot-key={slotKey}
				data-testid={`signature-anchor-${slotKey}`}
			>
				<Band ref={bandRef} $heightMm={heightMm} data-testid="signature-anchor-band">
					<Stamp
						ref={stampRef}
						$widthMm={widthMm}
						$offsetXMm={offsetXMm}
						$selected={selected}
						$editable={editor.isEditable}
						$liftYPx={draft?.liftYPx ?? 0}
						// Marks the stamp rather than its band, so a drop measures which half of
						// the STAMP it landed on. The band spans the page and would answer
						// "right" for a drop plainly left of a stamp the author moved across.
						{...{ [SIGNATURE_STAMP_ATTRIBUTE]: '' }}
						data-testid="signature-anchor-stamp"
						onPointerDown={(event) => {
							// The whole stamp is the handle. Only the resize corner is carved
							// out of it — everything else is one drag whose direction decides
							// whether the anchor is moving across its line or off it.
							const from = event.target;
							if (
								from instanceof HTMLElement &&
								from.closest('[data-resize-handle]') !== null
							) {
								return;
							}
							beginGesture(event, 'stamp');
						}}
					>
						<StampPreview
							// A role since deleted leaves the document holding a slot nothing
							// describes. Drawn as an unnamed stamp rather than omitted, so the
							// author can see there is something here to remove.
							slotLabel={display?.label ?? 'Unassigned signature'}
							isWitness={isWitness}
							roleType={display?.roleType ?? DocumentSigningRoleType.PARTY}
							// An anchor is always a signature. The date boxes DC-1000 added are
							// placed on the PDF canvas, not as nodes in a reflow document.
							kind="signature"
							showMetadata={showMetadata}
							// The preview scales its own type off this, and 1 is the design
							// size — so a resized stamp gets type in proportion rather than
							// design-size text stranded in a smaller card.
							scale={Math.min(2, Math.max(0.3, widthMm / designWidthMm))}
						/>
						{editor.isEditable && (
							<ResizeGrip
								data-resize-handle
								contentEditable={false}
								aria-label="Resize this signature"
								data-testid="signature-anchor-resize"
								onPointerDown={(event) => beginGesture(event, 'resize')}
							/>
						)}
					</Stamp>
				</Band>
			</NodeViewWrapper>
		);
	};

/**
 * The full-width row the anchor owns.
 *
 * Its height is the stamp's, so the text below moves as the stamp is resized — and because the
 * band is never narrower than the line, no paragraph can ever be laid out beside a stamp. That
 * is the invariant the whole placement model rests on, expressed in one CSS rule.
 */
const Band = styled.div<{ $heightMm: number }>`
	position: relative;
	width: 100%;
	height: ${({ $heightMm }) => $heightMm}mm;
`;

const Stamp = styled.div<{
	$widthMm: number;
	$offsetXMm: number;
	$selected: boolean;
	$editable: boolean;
	$liftYPx: number;
}>`
	position: absolute;
	top: 0;
	left: ${({ $offsetXMm }) => $offsetXMm}mm;
	width: ${({ $widthMm }) => $widthMm}mm;
	height: 100%;
	max-width: 100%;
	border-radius: 4px;
	cursor: ${({ $editable }) => ($editable ? 'grab' : 'default')};
	touch-action: none;

	/* Lifted off its own line while it is being carried, so the space it came from stays open
	   and the drop line beneath is not hidden by the thing being dropped. A transform, so
	   nothing around it reflows for what is only a preview.

	   The pointer-events rule is the load-bearing part. The stamp follows the pointer, so it is
	   the topmost thing UNDER it — and every question this gesture asks ("what is here, and can
	   the anchor go there?") is a hit test at that point. Without this they all answer "the
	   anchor you are dragging", which is a self-drop, which is refused: the drag looks alive and
	   then puts the stamp straight back. The pointer is captured, so events still arrive here. */
	${({ $liftYPx }) =>
		$liftYPx === 0
			? ''
			: `transform: translateY(${$liftYPx}px); opacity: 0.75; z-index: 3;
			   pointer-events: none; cursor: grabbing;`}
	// The ring is a shadow, not a border: a border would add to the box and make the anchor
	// occupy more room when selected than the renderer reserves for it.
	box-shadow: ${({ $selected }) =>
		$selected ? `0 0 0 2px ${PALETTE.brand}, 0 0 0 4px rgba(13, 94, 242, 0.25)` : 'none'};

	// The stamp preview lays itself out against its container, and the container here is a
	// physical size rather than a pixel one.
	& > * {
		width: 100%;
		height: 100%;
	}

	&:hover [data-resize-handle] {
		opacity: 1;
	}
`;

const ResizeGrip = styled.span`
	position: absolute;
	right: -6px;
	bottom: -6px;
	z-index: 2;
	width: 12px;
	height: 12px;
	border: 2px solid ${PALETTE.brand};
	border-radius: 2px;
	background: #ffffff;
	cursor: nwse-resize;
	opacity: 0;
	touch-action: none;
	transition: opacity 120ms ease;
`;
